/**
 * CrossProjectKPIs — metric tiles + sparkline.
 *
 * Monochrome chrome (ADR-057): chrome stays gray, status colour
 * survives only on the delta indicator (green up = good change, red
 * down = bad change). The sparkline traces in foreground/40%.
 */
import { ArrowDown, ArrowRight, ArrowUp } from 'lucide-react';

import { cn } from '../../lib/utils';
import type { CrossProjectKPI, CrossProjectKPIsResponse } from './dashboard-types';

export interface CrossProjectKPIsProps {
  data: CrossProjectKPIsResponse | undefined;
  isLoading?: boolean;
  onChangeWindow?: (window: '7d' | '30d' | '90d') => void;
  className?: string;
}

export function CrossProjectKPIs({
  data,
  isLoading,
  onChangeWindow,
  className,
}: CrossProjectKPIsProps) {
  const window = data?.window ?? '30d';
  const kpis = data?.kpis ?? [];
  return (
    <section
      aria-labelledby="kpis-heading"
      className={cn('rounded-md border border-border bg-background p-4', className)}
    >
      <header className="mb-3 flex items-baseline justify-between">
        <h2 id="kpis-heading" className="text-sm font-semibold text-foreground">
          Cross-project KPIs
        </h2>
        <select
          value={window}
          onChange={(e) => onChangeWindow?.(e.target.value as '7d' | '30d' | '90d')}
          aria-label="Window"
          className="h-7 rounded border border-border bg-background px-1 text-xs text-foreground focus:border-foreground/30 focus:outline-none"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
        </select>
      </header>
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : kpis.length === 0 ? (
        <p className="rounded border border-dashed border-border bg-foreground/[0.02] p-4 text-center text-xs text-muted-foreground">
          No data yet.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {kpis.map((k) => (
            <KpiTile key={k.id} kpi={k} />
          ))}
        </div>
      )}
    </section>
  );
}

function KpiTile({ kpi }: { kpi: CrossProjectKPI }) {
  const delta = kpi.delta_vs_prior_pct;
  const Trend =
    delta == null ? ArrowRight : delta > 0 ? ArrowUp : delta < 0 ? ArrowDown : ArrowRight;
  const trendTone =
    delta == null
      ? 'text-muted-foreground'
      : delta > 0
        ? 'text-emerald-600 dark:text-emerald-400'
        : delta < 0
          ? 'text-rose-600 dark:text-rose-400'
          : 'text-muted-foreground';

  return (
    <div className="rounded border border-border bg-foreground/[0.02] p-3">
      <div className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
        {kpi.label}
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-xl font-semibold text-foreground">{formatKpiValue(kpi.value)}</span>
        <span className="text-2xs text-muted-foreground">{kpi.unit}</span>
      </div>
      <div className={cn('mt-1 inline-flex items-center gap-1 text-2xs', trendTone)}>
        <Trend size={10} aria-hidden />
        {delta == null ? '—' : `${delta > 0 ? '+' : ''}${delta}%`}
      </div>
      {kpi.sparkline && kpi.sparkline.length > 1 && <Sparkline values={kpi.sparkline} />}
    </div>
  );
}

function Sparkline({ values }: { values: number[] }) {
  const w = 120;
  const h = 24;
  const max = Math.max(...values, 1);
  const step = values.length > 1 ? w / (values.length - 1) : 0;
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`)
    .join(' ');
  return (
    <svg
      role="img"
      aria-label="sparkline"
      viewBox={`0 0 ${w} ${h}`}
      className="mt-1 h-6 w-full text-foreground/40"
    >
      <polyline fill="none" stroke="currentColor" strokeWidth="1.5" points={points} />
    </svg>
  );
}

function formatKpiValue(v: number): string {
  if (Math.abs(v) >= 1000) return v.toLocaleString();
  if (Number.isInteger(v)) return v.toString();
  return v.toFixed(1);
}
